import { useReducedMotion } from 'framer-motion';
import { gsap } from 'gsap';
import {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef,
	useState,
	type PointerEvent,
	type KeyboardEvent as ReactKeyboardEvent,
	type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';

import {
	Box,
	Button,
	CursorClickOutline,
	ExclamationOutline,
	Flex,
	HStack,
	IconButton,
	PlantOutline,
	Text,
	TrophyOutline,
	VolumeOffOutline,
	VolumeUpOutline,
	XOutline,
} from '@bonusly/design-system';

import { Bizy, type BizyState } from '../../bizy';
import BizyFlyMusic from '../assets/bizyfly.mp3';
import FlowerGrabSound from '../assets/flower-grab.mp3';
import { formatBizySwarmHeight, getBizySwarmDailySeed, loadBizySwarmStats, saveBizySwarmRun, type BizySwarmSavedRun } from '../lib/bizy-swarm-stats';

const PLAYER_RADIUS = 30;
const FLOWER_RADIUS = 22;
const WASP_RADIUS = 26;
const WORLD_WALL_PADDING = 42;
const STARTING_NECTAR = 5;
const MIN_LAUNCH_POWER = 800;
const MAX_LAUNCH_POWER = 6000;
const CHARGE_TIME_SECONDS = 0.9;
const VELOCITY_DAMPING_PER_SECOND = 0.48;
const SETTLED_SPEED = 26;
const FLYING_SPEED = 240;
const OUT_OF_NECTAR_GRACE_SECONDS = 0.7;
const PHYSICS_SUBSTEPS = 3;
const PIXELS_PER_METER = 18;
const BAND_HEIGHT = 360;
const FRAME_RATE = 45;
const MAX_DPR = 2;
const WALL_BOUNCE = 0.94;
const HOVER_BOB_PIXELS = 7;
const HOVER_DRIFT_PIXELS = 3;
const FALL_SPEED = 760;
const DANGER_EMOJIS = ['🐛', '🪲', '🦂', '🕷️', '🚗', '🔪', '💣', '⚠️'];

type Phase = 'intro' | 'playing' | 'gameOver';
type EndReason = 'nectar' | 'wasp';
type WaspType = 'hover' | 'patrol';

interface Point {
	x: number;
	y: number;
}

interface VelocityPoint extends Point {
	vx: number;
	vy: number;
}

interface Flower extends Point {
	id: number;
	phase: number;
	scale: number;
}

interface Wasp extends Point {
	id: number;
	emoji: string;
	baseX: number;
	baseY: number;
	amplitude: number;
	speed: number;
	phase: number;
	type: WaspType;
	scored: boolean;
}

interface PickupBurst extends Point {
	id: number;
	label: string;
}

interface Viewport {
	width: number;
	height: number;
	dpr: number;
}

interface MutableGameState {
	elapsed: number;
	cameraY: number;
	nextBandY: number;
	maxHeight: number;
	nectar: number;
	currentLaunchFlowerHits: number;
	launchInProgress: boolean;
	flowersCollected: number;
	launches: number;
	waspsDodged: number;
	lastRenderAt: number;
	endReason: EndReason | null;
	endHazardEmoji: string | null;
	endAt: number | null;
	player: VelocityPoint & {
		hovering: boolean;
		hoverPhase: number;
		hit: boolean;
	};
	pointer: {
		x: number;
		y: number;
		charging: boolean;
		chargeStartedAt: number;
	};
	flowers: Flower[];
	wasps: Wasp[];
}

interface BizyRenderState {
	x: number;
	y: number;
	tilt: number;
	state: BizyState;
	speed: number;
	hovering: boolean;
	charging: boolean;
}

interface RenderState {
	heightMeters: number;
	bestHeightMeters: number;
	nectar: number;
	flowersCollected: number;
	launches: number;
	waspsDodged: number;
	endReason: EndReason | null;
	endHazardEmoji: string | null;
	bizy: BizyRenderState;
}

interface BizySwarmGameProps {
	onClose: () => void;
}

const createInitialRenderState = (): RenderState => {
	const width = typeof window === 'undefined' ? 1024 : window.innerWidth;
	const height = typeof window === 'undefined' ? 768 : window.innerHeight;

	return {
		heightMeters: 0,
		bestHeightMeters: 0,
		nectar: STARTING_NECTAR,
		flowersCollected: 0,
		launches: 0,
		waspsDodged: 0,
		endReason: null,
		endHazardEmoji: null,
		bizy: {
			x: width / 2,
			y: height * 0.65,
			tilt: 0,
			state: 'curious',
			speed: 0,
			hovering: true,
			charging: false,
		},
	};
};

const getDistance = (a: Point, b: Point): number => Math.hypot(a.x - b.x, a.y - b.y);

const clamp = (value: number, min: number, max: number): number => Math.max(min, Math.min(max, value));

const hashString = (value: string): number => {
	let hash = 2166136261;
	for (let i = 0; i < value.length; i += 1) {
		hash ^= value.charCodeAt(i);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
};

const createSeededRandom = (seed: string): (() => number) => {
	let state = hashString(seed) || 1;
	return () => {
		state = (state * 1664525 + 1013904223) >>> 0;
		return state / 4294967296;
	};
};

const canUseHtmlAudioElement = (): boolean => typeof window !== 'undefined' && !window.navigator.userAgent.toLowerCase().includes('jsdom');

const easeOutQuad = (value: number): number => 1 - (1 - value) * (1 - value);

const getHeightMeters = (height: number): number => Math.max(0, Math.floor(height / PIXELS_PER_METER));

const screenY = (game: MutableGameState, viewport: Viewport, worldY: number): number => viewport.height - (worldY - game.cameraY);

const getPlayerScreenPoint = (game: MutableGameState, viewport: Viewport): Point => {
	const bob = game.player.hovering && !game.pointer.charging && !game.player.hit ? Math.sin(game.player.hoverPhase) * HOVER_BOB_PIXELS : 0;
	const drift = game.player.hovering && !game.pointer.charging && !game.player.hit ? Math.sin(game.player.hoverPhase * 0.42) * HOVER_DRIFT_PIXELS : 0;

	return {
		x: game.player.x + drift,
		y: screenY(game, viewport, game.player.y) + bob,
	};
};

const getChargeRatio = (game: MutableGameState): number =>
	game.pointer.charging ? clamp((game.elapsed - game.pointer.chargeStartedAt) / CHARGE_TIME_SECONDS, 0, 1) : 0;

const getLaunchPower = (chargeRatio: number): number => MIN_LAUNCH_POWER + (MAX_LAUNCH_POWER - MIN_LAUNCH_POWER) * easeOutQuad(chargeRatio);

const getAimVector = (game: MutableGameState, viewport: Viewport): Point => {
	const playerScreen = getPlayerScreenPoint(game, viewport);
	const dx = game.pointer.x - playerScreen.x;
	const dy = game.pointer.y - playerScreen.y;
	const length = Math.hypot(dx, dy);

	if (length < 8) return { x: 0, y: 1 };

	return {
		x: dx / length,
		y: -dy / length,
	};
};

const moveWithWalls = (point: VelocityPoint, viewport: Viewport, deltaSeconds: number) => {
	point.x += point.vx * deltaSeconds;
	point.y += point.vy * deltaSeconds;

	const minX = WORLD_WALL_PADDING;
	const maxX = viewport.width - WORLD_WALL_PADDING;
	if (point.x < minX) {
		point.x = minX;
		point.vx = Math.abs(point.vx) * WALL_BOUNCE;
	}
	if (point.x > maxX) {
		point.x = maxX;
		point.vx = -Math.abs(point.vx) * WALL_BOUNCE;
	}
};

const drawFlower = (ctx: CanvasRenderingContext2D, flower: Flower, x: number, y: number, elapsed: number) => {
	const sway = Math.sin(elapsed * 1.35 + flower.phase);
	const scale = flower.scale + sway * 0.025;
	ctx.save();
	ctx.translate(x + sway * 5, y);
	ctx.rotate(sway * 0.08);
	ctx.scale(scale, scale);
	ctx.font = '46px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.shadowColor = 'rgba(29, 31, 37, 0.25)';
	ctx.shadowBlur = 10;
	ctx.shadowOffsetY = 4;
	ctx.fillText('🌸', 0, 0);
	ctx.restore();
};

const drawWasp = (ctx: CanvasRenderingContext2D, wasp: Wasp, x: number, y: number) => {
	ctx.save();
	ctx.translate(x, y);
	ctx.rotate(Math.sin(wasp.phase) * 0.12);
	ctx.font = '48px "Apple Color Emoji", "Segoe UI Emoji", "Noto Color Emoji", sans-serif';
	ctx.textAlign = 'center';
	ctx.textBaseline = 'middle';
	ctx.shadowColor = 'rgba(29, 31, 37, 0.34)';
	ctx.shadowBlur = 12;
	ctx.shadowOffsetY = 5;
	ctx.fillText(wasp.emoji, 0, 0);
	ctx.restore();
};

const drawBackground = (ctx: CanvasRenderingContext2D, game: MutableGameState, viewport: Viewport) => {
	const altitude = getHeightMeters(game.maxHeight);
	const skyMix = clamp(altitude / 150, 0, 1);
	const spaceMix = clamp((altitude - 260) / 260, 0, 1);
	const gradient = ctx.createLinearGradient(0, 0, 0, viewport.height);

	gradient.addColorStop(
		0,
		`rgba(${Math.round(20 + 112 * (1 - spaceMix))}, ${Math.round(29 + 126 * (1 - spaceMix))}, ${Math.round(65 + 166 * (1 - spaceMix))}, 1)`
	);
	gradient.addColorStop(
		0.48,
		`rgba(${Math.round(88 + 90 * (1 - spaceMix))}, ${Math.round(165 + 42 * (1 - spaceMix))}, ${Math.round(236 + 10 * (1 - spaceMix))}, 1)`
	);
	gradient.addColorStop(1, `rgba(${Math.round(180 - 92 * skyMix)}, ${Math.round(232 - 67 * skyMix)}, ${Math.round(248 - 12 * skyMix)}, 1)`);
	ctx.fillStyle = gradient;
	ctx.fillRect(0, 0, viewport.width, viewport.height);

	if (spaceMix > 0) {
		ctx.save();
		ctx.globalAlpha = spaceMix;
		ctx.fillStyle = '#f7fbff';
		const firstRow = Math.floor(game.cameraY / 160) - 1;
		const lastRow = Math.ceil((game.cameraY + viewport.height) / 160) + 1;
		for (let row = firstRow; row <= lastRow; row += 1) {
			for (let i = 0; i < 10; i += 1) {
				const seed = hashString(`${row}:${i}`);
				const x = ((seed % 1000) / 1000) * viewport.width;
				const y = screenY(game, viewport, row * 160 + ((seed >>> 10) % 150));
				const radius = 0.8 + ((seed >>> 20) % 18) / 10;
				ctx.beginPath();
				ctx.arc(x, y, radius, 0, Math.PI * 2);
				ctx.fill();
			}
		}
		ctx.restore();
	}

	if (game.cameraY < 220) {
		const groundY = screenY(game, viewport, -80);
		ctx.fillStyle = 'rgba(68, 176, 92, 0.38)';
		ctx.beginPath();
		ctx.moveTo(0, groundY);
		ctx.bezierCurveTo(viewport.width * 0.22, groundY - 90, viewport.width * 0.48, groundY + 20, viewport.width, groundY - 72);
		ctx.lineTo(viewport.width, viewport.height);
		ctx.lineTo(0, viewport.height);
		ctx.closePath();
		ctx.fill();
	}
};

const drawTrajectory = (ctx: CanvasRenderingContext2D, game: MutableGameState, viewport: Viewport) => {
	if (!game.pointer.charging || game.nectar <= 0 || game.endReason) return;

	const aim = getAimVector(game, viewport);
	const player = getPlayerScreenPoint(game, viewport);
	const screenAim = { x: aim.x, y: -aim.y };
	const tipDistance = PLAYER_RADIUS + 72;
	const tip = {
		x: player.x + screenAim.x * tipDistance,
		y: player.y + screenAim.y * tipDistance,
	};
	const normal = { x: -screenAim.y, y: screenAim.x };
	const headLength = 24;
	const headWidth = 15;

	ctx.save();
	ctx.lineCap = 'round';
	ctx.lineJoin = 'round';
	ctx.shadowColor = 'rgba(29, 31, 37, 0.28)';
	ctx.shadowBlur = 8;
	ctx.shadowOffsetY = 3;
	ctx.fillStyle = 'rgba(255, 255, 255, 0.96)';
	ctx.beginPath();
	ctx.moveTo(tip.x, tip.y);
	ctx.lineTo(tip.x - screenAim.x * headLength + normal.x * headWidth, tip.y - screenAim.y * headLength + normal.y * headWidth);
	ctx.lineTo(tip.x - screenAim.x * headLength - normal.x * headWidth, tip.y - screenAim.y * headLength - normal.y * headWidth);
	ctx.closePath();
	ctx.fill();
	ctx.restore();
};

const drawChargeRing = (ctx: CanvasRenderingContext2D, game: MutableGameState, viewport: Viewport) => {
	if (!game.pointer.charging || game.nectar <= 0 || game.endReason) return;

	const player = getPlayerScreenPoint(game, viewport);
	const chargeRatio = getChargeRatio(game);
	ctx.save();
	ctx.lineWidth = 6;
	ctx.strokeStyle = 'rgba(255, 255, 255, 0.36)';
	ctx.beginPath();
	ctx.arc(player.x, player.y, PLAYER_RADIUS + 16, 0, Math.PI * 2);
	ctx.stroke();
	ctx.strokeStyle = 'rgba(34, 185, 79, 0.96)';
	ctx.beginPath();
	ctx.arc(player.x, player.y, PLAYER_RADIUS + 16, -Math.PI / 2, -Math.PI / 2 + Math.PI * 2 * chargeRatio);
	ctx.stroke();
	ctx.restore();
};

const drawScene = (ctx: CanvasRenderingContext2D, game: MutableGameState, viewport: Viewport) => {
	ctx.setTransform(viewport.dpr, 0, 0, viewport.dpr, 0, 0);
	ctx.clearRect(0, 0, viewport.width, viewport.height);
	drawBackground(ctx, game, viewport);
	drawTrajectory(ctx, game, viewport);

	game.flowers.forEach((flower) => {
		const y = screenY(game, viewport, flower.y);
		if (y < -60 || y > viewport.height + 60) return;
		drawFlower(ctx, flower, flower.x, y, game.elapsed);
	});

	game.wasps.forEach((wasp) => {
		const y = screenY(game, viewport, wasp.y);
		if (y < -70 || y > viewport.height + 70) return;
		drawWasp(ctx, wasp, wasp.x, y);
	});

	drawChargeRing(ctx, game, viewport);
};

const getBizyRenderState = (game: MutableGameState, viewport: Viewport, bestHeightMeters: number): RenderState => {
	const speed = Math.hypot(game.player.vx, game.player.vy);
	const playerScreen = getPlayerScreenPoint(game, viewport);
	const chargeRatio = getChargeRatio(game);
	const aim = getAimVector(game, viewport);
	const movingRight = game.player.vx >= 0;
	const state: BizyState =
		game.player.hit || game.endReason === 'nectar'
			? 'disappointed'
			: game.pointer.charging
				? 'thinking'
				: speed > FLYING_SPEED
					? movingRight
						? 'flyingRight'
						: 'flyingLeft'
					: 'curious';
	const tilt = game.player.hit ? 0.45 : game.pointer.charging ? clamp(aim.x * 0.42, -0.42, 0.42) : clamp(game.player.vx / 1050, -0.48, 0.48);

	return {
		heightMeters: getHeightMeters(game.maxHeight),
		bestHeightMeters,
		nectar: game.nectar,
		flowersCollected: game.flowersCollected,
		launches: game.launches,
		waspsDodged: game.waspsDodged,
		endReason: game.endReason,
		endHazardEmoji: game.endHazardEmoji,
		bizy: {
			x: playerScreen.x,
			y: playerScreen.y + (game.pointer.charging ? Math.sin(chargeRatio * Math.PI) * 3 : 0),
			tilt,
			state,
			speed,
			hovering: game.player.hovering,
			charging: game.pointer.charging,
		},
	};
};

export function BizySwarmGame({ onClose }: BizySwarmGameProps) {
	const { t } = useTranslation('admin_bizy');
	const shouldReduceMotion = useReducedMotion() ?? false;
	const dailySeed = useMemo(() => getBizySwarmDailySeed(), []);
	const rngRef = useRef(createSeededRandom(dailySeed));
	const rootRef = useRef<HTMLDivElement>(null);
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const musicRef = useRef<HTMLAudioElement>(null);
	const flowerGrabSoundRef = useRef<HTMLAudioElement>(null);
	const previousFocusRef = useRef<HTMLElement | null>(null);
	const musicContextRef = useRef<AudioContext | null>(null);
	const musicBufferRef = useRef<AudioBuffer | null>(null);
	const musicSourceRef = useRef<AudioBufferSourceNode | null>(null);
	const musicGainRef = useRef<GainNode | null>(null);
	const musicPlayRequestIdRef = useRef(0);
	const scoreTweenRef = useRef<ReturnType<typeof gsap.to> | null>(null);
	const isMusicEnabledRef = useRef(true);
	const phaseRef = useRef<Phase>('intro');
	const keyboardAimRef = useRef<Point>({ x: 0, y: 1 });
	const gameRef = useRef<MutableGameState | null>(null);
	const viewportRef = useRef<Viewport>({
		width: typeof window === 'undefined' ? 1024 : window.innerWidth,
		height: typeof window === 'undefined' ? 768 : window.innerHeight,
		dpr: typeof window === 'undefined' ? 1 : Math.min(window.devicePixelRatio || 1, MAX_DPR),
	});
	const idRef = useRef(1);
	const [phase, setPhase] = useState<Phase>('intro');
	const [result, setResult] = useState<BizySwarmSavedRun | null>(null);
	const [bestHeightMeters, setBestHeightMeters] = useState(() => loadBizySwarmStats().bestScore);
	const [animatedHeight, setAnimatedHeight] = useState(0);
	const [isMusicEnabled, setIsMusicEnabled] = useState(true);
	const [renderState, setRenderState] = useState<RenderState>(() => ({
		...createInitialRenderState(),
		bestHeightMeters: loadBizySwarmStats().bestScore,
	}));
	const [pickupBursts, setPickupBursts] = useState<PickupBurst[]>([]);

	useEffect(() => {
		isMusicEnabledRef.current = isMusicEnabled;
		phaseRef.current = phase;
	}, [isMusicEnabled, phase]);

	useEffect(
		() => () => {
			scoreTweenRef.current?.kill();
			scoreTweenRef.current = null;
		},
		[]
	);

	const pauseMusic = useCallback(() => {
		musicPlayRequestIdRef.current += 1;
		try {
			musicSourceRef.current?.stop();
		} catch {
			// The source may already be stopped by a previous pause.
		}
		musicSourceRef.current = null;
		const music = musicRef.current;
		if (music && canUseHtmlAudioElement()) {
			gsap.killTweensOf(music);
			try {
				music.pause();
			} catch {
				// jsdom and some browser states can reject media controls; stopping sound is best-effort.
			}
			music.playbackRate = 1;
		}
	}, []);

	const playMusic = useCallback(() => {
		if (!isMusicEnabled) return;

		const playRequestId = musicPlayRequestIdRef.current + 1;
		musicPlayRequestIdRef.current = playRequestId;
		const canPlayRequest = () => musicPlayRequestIdRef.current === playRequestId && isMusicEnabledRef.current && phaseRef.current === 'playing';
		const fallbackToAudioElement = () => {
			const music = musicRef.current;
			if (!music || !canUseHtmlAudioElement() || !canPlayRequest()) return;
			gsap.killTweensOf(music);
			music.volume = 0.38;
			music.playbackRate = 1;
			void music.play().catch(() => {
				// Browsers can reject autoplay until the next direct user gesture.
			});
		};

		const playWebAudioLoop = async () => {
			const AudioContextConstructor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
			if (!AudioContextConstructor) {
				fallbackToAudioElement();
				return;
			}

			const context = musicContextRef.current ?? new AudioContextConstructor();
			musicContextRef.current = context;
			if (context.state === 'suspended') await context.resume();
			if (!musicBufferRef.current) {
				const response = await fetch(BizyFlyMusic);
				const arrayBuffer = await response.arrayBuffer();
				musicBufferRef.current = await context.decodeAudioData(arrayBuffer);
			}
			if (!canPlayRequest()) return;

			musicSourceRef.current?.stop();
			const source = context.createBufferSource();
			const gain = musicGainRef.current ?? context.createGain();
			gain.gain.value = 0.38;
			musicGainRef.current = gain;
			source.buffer = musicBufferRef.current;
			source.loop = true;
			source.connect(gain);
			gain.connect(context.destination);
			source.start();
			musicSourceRef.current = source;
		};

		void playWebAudioLoop().catch(fallbackToAudioElement);
	}, [isMusicEnabled]);

	const handleToggleMusic = useCallback(() => {
		setIsMusicEnabled((enabled) => !enabled);
	}, []);

	const bendMusicForDeath = useCallback(() => {
		const context = musicContextRef.current;
		const source = musicSourceRef.current;
		const gain = musicGainRef.current;
		if (context && source && gain) {
			const now = context.currentTime;
			gain.gain.cancelScheduledValues(now);
			source.playbackRate.cancelScheduledValues(now);
			gain.gain.setValueAtTime(gain.gain.value, now);
			source.playbackRate.setValueAtTime(source.playbackRate.value, now);
			gain.gain.linearRampToValueAtTime(0.04, now + 0.6);
			source.playbackRate.linearRampToValueAtTime(0.55, now + 0.6);
			return;
		}

		const music = musicRef.current;
		if (!music || music.paused) return;
		gsap.to(music, { volume: 0.04, playbackRate: 0.55, duration: 0.6, ease: 'power2.out' });
	}, []);

	const playFlowerGrabSound = useCallback(() => {
		const sound = flowerGrabSoundRef.current;
		if (!sound || !isMusicEnabled || !canUseHtmlAudioElement()) return;
		sound.volume = 0.68;
		sound.currentTime = 0;
		void sound.play().catch(() => {
			// Pickup sounds are best-effort if the browser blocks audio.
		});
	}, [isMusicEnabled]);

	useEffect(() => {
		if (phase === 'playing' && isMusicEnabled) {
			playMusic();
			return;
		}

		pauseMusic();
	}, [isMusicEnabled, pauseMusic, phase, playMusic]);

	useEffect(() => pauseMusic, [pauseMusic]);

	useLayoutEffect(() => {
		if (!rootRef.current) return;
		gsap.fromTo(rootRef.current, { opacity: 0, scale: 0.985 }, { opacity: 1, scale: 1, duration: shouldReduceMotion ? 0 : 0.28, ease: 'power3.out' });
	}, [shouldReduceMotion]);

	useEffect(() => {
		const previousOverflow = document.body.style.overflow;
		previousFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null;
		document.body.style.overflow = 'hidden';
		requestAnimationFrame(() => rootRef.current?.focus());
		return () => {
			document.body.style.overflow = previousOverflow;
			previousFocusRef.current?.focus();
		};
	}, []);

	const handleRootKeyDown = useCallback((event: ReactKeyboardEvent<HTMLDivElement>) => {
		if (event.key !== 'Tab') return;

		const root = rootRef.current;
		if (!root) return;

		const focusable = Array.from(root.querySelectorAll<HTMLElement>('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')).filter(
			(element) => !element.hasAttribute('disabled') && element.getAttribute('aria-hidden') !== 'true'
		);

		if (focusable.length === 0) {
			event.preventDefault();
			root.focus();
			return;
		}

		const first = focusable[0];
		const last = focusable[focusable.length - 1];
		const active = document.activeElement;

		if (event.shiftKey && active === first) {
			event.preventDefault();
			last.focus();
			return;
		}

		if (!event.shiftKey && active === last) {
			event.preventDefault();
			first.focus();
			return;
		}

		if (!focusable.includes(active as HTMLElement)) {
			event.preventDefault();
			(event.shiftKey ? last : first).focus();
		}
	}, []);

	const resizeCanvas = useCallback(() => {
		const viewport = {
			width: window.innerWidth,
			height: window.innerHeight,
			dpr: Math.min(window.devicePixelRatio || 1, MAX_DPR),
		};
		viewportRef.current = viewport;
		const canvas = canvasRef.current;
		if (!canvas) return;
		canvas.width = Math.floor(viewport.width * viewport.dpr);
		canvas.height = Math.floor(viewport.height * viewport.dpr);
		canvas.style.width = `${viewport.width}px`;
		canvas.style.height = `${viewport.height}px`;

		const game = gameRef.current;
		if (game) {
			game.player.x = clamp(game.player.x, WORLD_WALL_PADDING, viewport.width - WORLD_WALL_PADDING);
			game.cameraY = game.player.y - viewport.height * 0.35;
			const context = canvas.getContext('2d');
			if (context) drawScene(context, game, viewport);
		}
	}, []);

	useLayoutEffect(() => {
		resizeCanvas();
		window.addEventListener('resize', resizeCanvas);
		return () => window.removeEventListener('resize', resizeCanvas);
	}, [resizeCanvas]);

	const addPickupBurst = useCallback((point: Point, label: string) => {
		const id = idRef.current;
		idRef.current += 1;
		setPickupBursts((prev) => [...prev.slice(-8), { id, label, x: point.x, y: point.y }]);
		window.setTimeout(() => {
			setPickupBursts((prev) => prev.filter((burst) => burst.id !== id));
		}, 750);
	}, []);

	const spawnBand = useCallback((game: MutableGameState, bandY: number, viewport: Viewport) => {
		const random = rngRef.current;
		const flowerCount = 1 + (random() > 0.58 ? 1 : 0);

		for (let i = 0; i < flowerCount; i += 1) {
			game.flowers.push({
				id: idRef.current,
				x: WORLD_WALL_PADDING + random() * Math.max(120, viewport.width - WORLD_WALL_PADDING * 2),
				y: bandY + 72 + random() * (BAND_HEIGHT - 120),
				phase: random() * Math.PI * 2,
				scale: 0.96 + random() * 0.1,
			});
			idRef.current += 1;
		}

		if (bandY < 260) return;

		const altitudeMeters = getHeightMeters(bandY);
		const density = clamp(0.35 + altitudeMeters / 420, 0.35, 0.92);
		const waspCount = random() < density ? 1 + (random() < density * 0.42 ? 1 : 0) : 0;
		for (let i = 0; i < waspCount; i += 1) {
			const type: WaspType = random() > 0.48 ? 'patrol' : 'hover';
			const baseX = WORLD_WALL_PADDING + random() * Math.max(120, viewport.width - WORLD_WALL_PADDING * 2);
			const baseY = bandY + 82 + random() * (BAND_HEIGHT - 130);
			game.wasps.push({
				id: idRef.current,
				emoji: DANGER_EMOJIS[Math.floor(random() * DANGER_EMOJIS.length)] ?? '🐛',
				x: baseX,
				y: baseY,
				baseX,
				baseY,
				amplitude: 36 + random() * Math.min(96, viewport.width * 0.16),
				speed: 1.1 + random() * 1.4,
				phase: random() * Math.PI * 2,
				type,
				scored: false,
			});
			idRef.current += 1;
		}
	}, []);

	const ensureWorld = useCallback(
		(game: MutableGameState, viewport: Viewport) => {
			const upperVisibleY = game.cameraY + viewport.height * 2.4;
			while (game.nextBandY < upperVisibleY) {
				spawnBand(game, game.nextBandY, viewport);
				game.nextBandY += BAND_HEIGHT;
			}
		},
		[spawnBand]
	);

	const updateRenderState = useCallback(() => {
		const game = gameRef.current;
		if (!game) return;
		setRenderState(getBizyRenderState(game, viewportRef.current, bestHeightMeters));
	}, [bestHeightMeters]);

	const updateKeyboardAim = useCallback(
		(direction: Point) => {
			const game = gameRef.current;
			if (!game || game.endReason) return;
			keyboardAimRef.current = direction;
			const player = getPlayerScreenPoint(game, viewportRef.current);
			game.pointer.x = player.x + direction.x * 180;
			game.pointer.y = player.y - direction.y * 180;
			updateRenderState();
		},
		[updateRenderState]
	);

	const finishGame = useCallback(
		(reason: EndReason) => {
			const game = gameRef.current;
			if (!game) return;
			const heightMeters = getHeightMeters(game.maxHeight);
			const savedRun = saveBizySwarmRun({
				score: heightMeters,
				flowersCollected: game.flowersCollected,
				launches: game.launches,
				waspsDodged: game.waspsDodged,
				dailySeed,
				durationSeconds: Math.round(game.elapsed),
				endReason: reason,
				endHazardEmoji: game.endHazardEmoji ?? undefined,
			});
			gameRef.current = null;
			setBestHeightMeters(savedRun.stats.bestScore);
			setRenderState((prev) => ({ ...prev, bestHeightMeters: savedRun.stats.bestScore, endReason: reason }));
			setResult(savedRun);
			setPhase('gameOver');
			setAnimatedHeight(0);
			scoreTweenRef.current?.kill();
			const animated = { value: 0 };
			scoreTweenRef.current = gsap.to(animated, {
				value: savedRun.run.score,
				duration: shouldReduceMotion ? 0 : 0.7,
				ease: 'power3.out',
				onUpdate: () => setAnimatedHeight(Math.round(animated.value)),
			});
		},
		[dailySeed, shouldReduceMotion]
	);

	const queueEnd = useCallback(
		(game: MutableGameState, reason: EndReason, delaySeconds: number) => {
			if (game.endReason) return;
			game.endReason = reason;
			game.endAt = game.elapsed + delaySeconds;
			if (reason === 'wasp') game.player.hit = true;
			bendMusicForDeath();
			updateRenderState();
		},
		[bendMusicForDeath, updateRenderState]
	);

	const startGame = useCallback(() => {
		const viewport = viewportRef.current;
		scoreTweenRef.current?.kill();
		scoreTweenRef.current = null;
		rngRef.current = createSeededRandom(`${dailySeed}:${viewport.width}x${viewport.height}`);
		idRef.current = 1;
		const centerX = viewport.width / 2;
		const game: MutableGameState = {
			elapsed: 0,
			cameraY: -viewport.height * 0.35,
			nextBandY: 120,
			maxHeight: 0,
			nectar: STARTING_NECTAR,
			currentLaunchFlowerHits: 0,
			launchInProgress: false,
			flowersCollected: 0,
			launches: 0,
			waspsDodged: 0,
			lastRenderAt: 0,
			endReason: null,
			endHazardEmoji: null,
			endAt: null,
			player: {
				x: centerX,
				y: 0,
				vx: 0,
				vy: 0,
				hovering: true,
				hoverPhase: 0,
				hit: false,
			},
			pointer: {
				x: centerX,
				y: viewport.height * 0.34,
				charging: false,
				chargeStartedAt: 0,
			},
			flowers: [],
			wasps: [],
		};

		ensureWorld(game, viewport);
		gameRef.current = game;
		setResult(null);
		setPickupBursts([]);
		setRenderState(getBizyRenderState(game, viewport, bestHeightMeters));
		setPhase('playing');
		playMusic();
		requestAnimationFrame(() => rootRef.current?.focus());
	}, [bestHeightMeters, dailySeed, ensureWorld, playMusic]);

	const releaseLaunch = useCallback(() => {
		const game = gameRef.current;
		if (!game || !game.pointer.charging) return;
		game.pointer.charging = false;
		if (game.nectar <= 0 || game.endReason) {
			updateRenderState();
			return;
		}

		const chargeRatio = getChargeRatio(game);
		const launchPower = getLaunchPower(chargeRatio);
		const aim = getAimVector(game, viewportRef.current);
		game.player.vx += aim.x * launchPower;
		game.player.vy += aim.y * launchPower;
		game.player.hovering = false;
		game.launchInProgress = true;
		game.currentLaunchFlowerHits = 0;
		game.nectar = Math.max(0, game.nectar - 1);
		addPickupBurst(getPlayerScreenPoint(game, viewportRef.current), t('game.nectarLost'));
		game.launches += 1;
		updateRenderState();
	}, [addPickupBurst, t, updateRenderState]);

	useEffect(() => {
		const isFromInteractiveControl = (target: EventTarget | null): boolean =>
			target instanceof HTMLElement && Boolean(target.closest('button, [href], input, select, textarea, [role="button"]'));

		const handleKeyDown = (event: globalThis.KeyboardEvent) => {
			if (event.key === 'Escape') {
				onClose();
				return;
			}
			if (event.key.toLowerCase() === 'r' && phase !== 'playing') {
				startGame();
				return;
			}
			if (phase !== 'playing') return;
			if (isFromInteractiveControl(event.target)) return;

			const key = event.key.toLowerCase();
			if (key === 'arrowleft' || key === 'a') {
				event.preventDefault();
				updateKeyboardAim({ x: -1, y: 0 });
				return;
			}
			if (key === 'arrowright' || key === 'd') {
				event.preventDefault();
				updateKeyboardAim({ x: 1, y: 0 });
				return;
			}
			if (key === 'arrowup' || key === 'w') {
				event.preventDefault();
				updateKeyboardAim({ x: 0, y: 1 });
				return;
			}
			if (key === 'arrowdown' || key === 's') {
				event.preventDefault();
				updateKeyboardAim({ x: 0, y: -1 });
				return;
			}
			if (key === ' ' || key === 'enter') {
				const game = gameRef.current;
				if (!game || game.pointer.charging || game.endReason || game.nectar <= 0 || event.repeat) return;
				event.preventDefault();
				const player = getPlayerScreenPoint(game, viewportRef.current);
				const direction = keyboardAimRef.current;
				game.pointer.x = player.x + direction.x * 180;
				game.pointer.y = player.y - direction.y * 180;
				game.pointer.charging = true;
				game.pointer.chargeStartedAt = game.elapsed;
				updateRenderState();
			}
		};
		const handleKeyUp = (event: globalThis.KeyboardEvent) => {
			const key = event.key.toLowerCase();
			if (phase === 'playing' && isFromInteractiveControl(event.target)) return;
			if (phase === 'playing' && (key === ' ' || key === 'enter')) {
				event.preventDefault();
				releaseLaunch();
			}
		};

		window.addEventListener('keydown', handleKeyDown);
		window.addEventListener('keyup', handleKeyUp);
		return () => {
			window.removeEventListener('keydown', handleKeyDown);
			window.removeEventListener('keyup', handleKeyUp);
		};
	}, [onClose, phase, releaseLaunch, startGame, updateKeyboardAim, updateRenderState]);

	useEffect(() => {
		if (phase !== 'playing') return;
		let previousTime = performance.now();
		let frameId = 0;

		const tick = (now: number) => {
			const game = gameRef.current;
			const canvas = canvasRef.current;
			const context = canvas?.getContext('2d');
			if (!game || !context) return;

			const viewport = viewportRef.current;
			const deltaSeconds = Math.min(0.034, (now - previousTime) / 1000);
			previousTime = now;
			const substepSeconds = deltaSeconds / PHYSICS_SUBSTEPS;

			for (let step = 0; step < PHYSICS_SUBSTEPS; step += 1) {
				game.elapsed += substepSeconds;
				game.player.hoverPhase += substepSeconds * (game.pointer.charging ? 5.4 : 3.2);

				game.wasps.forEach((wasp) => {
					const phaseValue = game.elapsed * wasp.speed + wasp.phase;
					wasp.x =
						wasp.type === 'patrol'
							? clamp(wasp.baseX + Math.sin(phaseValue) * wasp.amplitude, WORLD_WALL_PADDING, viewport.width - WORLD_WALL_PADDING)
							: wasp.baseX + Math.sin(phaseValue * 0.8) * 7;
					wasp.y = wasp.baseY + Math.sin(phaseValue * 1.2) * 9;
				});

				if (game.endReason === 'nectar') {
					game.player.hovering = false;
					game.player.vy = -FALL_SPEED;
					moveWithWalls(game.player, viewport, substepSeconds);
				} else if (!game.player.hit) {
					const damping = Math.pow(VELOCITY_DAMPING_PER_SECOND, substepSeconds);
					game.player.vx *= damping;
					game.player.vy *= damping;
					moveWithWalls(game.player, viewport, substepSeconds);
					game.maxHeight = Math.max(game.maxHeight, game.player.y);
					const speed = Math.hypot(game.player.vx, game.player.vy);
					game.player.hovering = speed < SETTLED_SPEED;

					game.flowers = game.flowers.filter((flower) => {
						if (!game.launchInProgress) return true;
						if (getDistance(game.player, flower) > PLAYER_RADIUS + FLOWER_RADIUS) return true;
						game.currentLaunchFlowerHits += 1;
						game.nectar += 1;
						game.flowersCollected += 1;
						playFlowerGrabSound();
						addPickupBurst({ x: flower.x, y: screenY(game, viewport, flower.y) }, t('game.flowerCollected'));
						if (!shouldReduceMotion) {
							gsap.fromTo(rootRef.current, { scale: 1 }, { scale: 1.004, duration: 0.08, yoyo: true, repeat: 1, ease: 'power2.out' });
						}
						return false;
					});

					if (game.launchInProgress && game.player.hovering && !game.pointer.charging) {
						game.launchInProgress = false;
						game.currentLaunchFlowerHits = 0;
					}

					for (const wasp of game.wasps) {
						if (!wasp.scored && game.player.y > wasp.baseY + 80) {
							wasp.scored = true;
							game.waspsDodged += 1;
						}
						if (getDistance(game.player, wasp) < PLAYER_RADIUS + WASP_RADIUS - 8) {
							game.endHazardEmoji = wasp.emoji;
							queueEnd(game, 'wasp', 0.65);
							break;
						}
					}

					if (game.nectar === 0 && game.player.hovering && !game.pointer.charging) {
						queueEnd(game, 'nectar', OUT_OF_NECTAR_GRACE_SECONDS);
					}
				}

				if (game.endReason && game.endAt !== null && game.elapsed >= game.endAt) {
					finishGame(game.endReason);
					return;
				}
			}

			const targetCameraY = Math.max(-viewport.height * 0.35, game.player.y - viewport.height * 0.35);
			game.cameraY += (targetCameraY - game.cameraY) * Math.min(1, deltaSeconds * 3.7);
			ensureWorld(game, viewport);
			game.flowers = game.flowers.filter((flower) => flower.y > game.cameraY - 300);
			game.wasps = game.wasps.filter((wasp) => wasp.baseY > game.cameraY - 360);

			drawScene(context, game, viewport);

			if (now - game.lastRenderAt > 1000 / FRAME_RATE) {
				game.lastRenderAt = now;
				updateRenderState();
			}

			frameId = requestAnimationFrame(tick);
		};

		frameId = requestAnimationFrame(tick);
		return () => cancelAnimationFrame(frameId);
	}, [addPickupBurst, ensureWorld, finishGame, phase, playFlowerGrabSound, queueEnd, shouldReduceMotion, t, updateRenderState]);

	const updatePointer = useCallback((event: PointerEvent<HTMLDivElement>) => {
		const game = gameRef.current;
		if (!game) return;
		game.pointer.x = event.clientX;
		game.pointer.y = event.clientY;
	}, []);

	const handlePointerDown = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			if (phase !== 'playing') return;
			const game = gameRef.current;
			if (!game || game.endReason || game.nectar <= 0) return;
			event.currentTarget.setPointerCapture(event.pointerId);
			updatePointer(event);
			game.pointer.charging = true;
			game.pointer.chargeStartedAt = game.elapsed;
			updateRenderState();
		},
		[phase, updatePointer, updateRenderState]
	);

	const handlePointerMove = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			if (phase !== 'playing') return;
			updatePointer(event);
		},
		[phase, updatePointer]
	);

	const handlePointerUp = useCallback(
		(event: PointerEvent<HTMLDivElement>) => {
			const game = gameRef.current;
			if (!game || !game.pointer.charging) return;
			updatePointer(event);
			releaseLaunch();
		},
		[releaseLaunch, updatePointer]
	);

	const handlePointerCancel = useCallback(() => {
		const game = gameRef.current;
		if (!game) return;
		game.pointer.charging = false;
		updateRenderState();
	}, [updateRenderState]);

	const bizyAnimation = {
		wingSpeed: renderState.bizy.speed > FLYING_SPEED ? 0.05 : renderState.bizy.charging ? 0.09 : 0.18,
		wingAmplitude: renderState.bizy.speed > FLYING_SPEED ? 0.9 : renderState.bizy.charging ? 0.72 : 0.36,
		hoverBob: renderState.bizy.hovering && !renderState.bizy.charging,
	};

	const game = (
		<Box
			ref={rootRef}
			tabIndex={-1}
			role="dialog"
			aria-modal="true"
			aria-label={t('game.introTitle')}
			position="fixed"
			inset={0}
			zIndex={2147483647}
			bg="bg.overlayBackdrop"
			backdropFilter="blur(3px)"
			overflow="hidden"
			cursor={phase === 'playing' ? (renderState.nectar > 0 ? 'crosshair' : 'default') : 'default'}
			onPointerDown={handlePointerDown}
			onPointerMove={handlePointerMove}
			onPointerUp={handlePointerUp}
			onPointerCancel={handlePointerCancel}
			onKeyDown={handleRootKeyDown}
			sx={{ touchAction: 'none' }}
			data-testid="bizy-swarm-game"
		>
			<canvas
				ref={canvasRef}
				aria-hidden
				style={{
					position: 'absolute',
					inset: 0,
					width: '100%',
					height: '100%',
					pointerEvents: 'none',
					opacity: phase === 'playing' ? 1 : 0,
					transition: 'opacity 500ms ease',
				}}
			/>
			<audio ref={musicRef} src={BizyFlyMusic} loop preload="auto" />
			<audio ref={flowerGrabSoundRef} src={FlowerGrabSound} preload="auto" />

			{phase === 'playing' && (
				<Flex position="absolute" top={4} left={4} right={4} align="flex-start" justify="space-between" gap={4} pointerEvents="none">
					<HStack spacing={6} pointerEvents="auto" align="flex-start" flexWrap="wrap">
						<PrimaryStat label={t('game.height')} value={formatBizySwarmHeight(renderState.heightMeters)} />
						<PrimaryStat label={t('game.nectar')} value={`${renderState.nectar}`} isDanger={renderState.nectar === 0} />
					</HStack>
					<Flex direction="column" align="flex-end" gap={3}>
						<HStack spacing={3} pointerEvents="auto">
							<Text color="text.onColor" fontSize="sm" fontWeight="bold" textAlign="right" whiteSpace="nowrap">
								{t('game.highScore', { score: formatBizySwarmHeight(renderState.bestHeightMeters) })}
							</Text>
							<MusicToggleButton
								isMusicEnabled={isMusicEnabled}
								onToggle={handleToggleMusic}
								musicOnLabel={t('game.musicOn')}
								musicOffLabel={t('game.musicOff')}
							/>
							<Button
								size="sm"
								h="48px"
								borderRadius="xl"
								variant="solid"
								colorScheme="secondary"
								onPointerDown={(event) => event.stopPropagation()}
								onClick={onClose}
							>
								{t('game.close')}
							</Button>
						</HStack>
					</Flex>
				</Flex>
			)}

			{phase === 'playing' && (
				<>
					<Box
						position="absolute"
						left={`${renderState.bizy.x}px`}
						top={`${renderState.bizy.y}px`}
						transform={`translate(-50%, -50%) rotate(${renderState.bizy.tilt}rad)`}
						filter={
							renderState.endReason === 'wasp'
								? 'drop-shadow(0 0 18px var(--chakra-colors-danger-bg))'
								: 'drop-shadow(0 10px 18px var(--chakra-colors-bg-overlayBackdrop))'
						}
						pointerEvents="none"
					>
						<Bizy state={renderState.bizy.state} size={92} animations={bizyAnimation} skipEntranceFade />
					</Box>
					{pickupBursts.map((burst) => (
						<PickupBurst key={burst.id} burst={burst} />
					))}
					<Text
						position="absolute"
						left={4}
						bottom={4}
						fontSize="xs"
						color="text.onColor"
						opacity={0.84}
						textShadow="0 1px 8px var(--chakra-colors-bg-overlayBackdrop)"
					>
						{renderState.nectar === 0 ? t('game.noNectarControls') : t('game.controls')}
					</Text>
				</>
			)}

			{phase === 'intro' && (
				<Flex position="absolute" inset={0} align="center" justify="center" px={4}>
					<Flex
						bg="bg.surface"
						borderRadius="2xl"
						border="1px solid"
						borderColor="border.soft"
						boxShadow="2xl"
						px={7}
						py={6}
						direction="column"
						align="center"
						gap={5}
						maxW="500px"
						w="100%"
						pointerEvents="auto"
						position="relative"
					>
						<Box position="absolute" top={4} right={4}>
							<MusicToggleButton
								isMusicEnabled={isMusicEnabled}
								onToggle={handleToggleMusic}
								musicOnLabel={t('game.musicOn')}
								musicOffLabel={t('game.musicOff')}
							/>
						</Box>
						<Bizy state="buzzing" size={150} />
						<Flex direction="column" align="center" gap={1}>
							<Text fontSize="2xl" fontWeight="black" color="text.default">
								{t('game.introTitle')}
							</Text>
							<Text color="text.soft" fontSize="md" fontWeight="semibold" textAlign="center">
								{t('game.introSubtitle')}
							</Text>
						</Flex>

						<Flex direction="column" gap={4} align="stretch" w="100%">
							<IntroRule icon={<CursorClickOutline boxSize={5} />} text={t('game.rules.aim')} />
							<IntroRule icon={<PlantOutline boxSize={5} />} text={t('game.rules.flowers')} />
							<IntroRule icon={<ExclamationOutline boxSize={5} />} text={t('game.rules.hazards')} />
						</Flex>

						<Flex bg="bg.subtle" borderRadius="lg" px={4} py={2} gap={2} align="center">
							<TrophyOutline boxSize={4} color="text.soft" />
							<Text color="text.soft" fontSize="sm">
								{t('game.bestClimb')}
							</Text>
							<Text color="text.default" fontSize="sm" fontWeight="black">
								{formatBizySwarmHeight(bestHeightMeters)}
							</Text>
						</Flex>

						<Flex gap={5} wrap="wrap" justify="center" align="center">
							<Button variant="ghost" colorScheme="secondary" onClick={onClose}>
								{t('game.neverMind')}
							</Button>
							<Button colorScheme="primary" onClick={startGame}>
								{t('game.start')}
							</Button>
						</Flex>
						<Text color="text.softer" fontSize="xs">
							{t('game.restartHint')}
						</Text>
					</Flex>
				</Flex>
			)}

			{phase === 'gameOver' && result && (
				<Flex position="absolute" inset={0} align="center" justify="center" px={4} onPointerDown={onClose}>
					<Flex
						bg="bg.surface"
						borderRadius="2xl"
						border="1px solid"
						borderColor="border.soft"
						boxShadow="2xl"
						p={6}
						direction="column"
						align="center"
						gap={4}
						maxW="430px"
						w="100%"
						pointerEvents="auto"
						position="relative"
						onPointerDown={(event) => event.stopPropagation()}
					>
						<IconButton
							aria-label={t('game.close')}
							icon={<XOutline boxSize={5} />}
							size="sm"
							variant="ghost"
							colorScheme="secondary"
							position="absolute"
							top={3}
							right={3}
							onClick={onClose}
						/>
						<Bizy state={result.run.endReason === 'wasp' ? 'disappointed' : result.isPersonalBest ? 'excited' : 'waving'} size="lg" />
						<Text fontSize="lg" color="text.soft" fontWeight="semibold" textAlign="center">
							{result.run.endReason === 'wasp'
								? t('game.hitHazard', { hazard: result.run.endHazardEmoji ?? t('game.hazardFallback') })
								: result.run.endReason === 'nectar'
									? t('game.ranOutOfNectar')
									: result.isPersonalBest
										? t('game.newBest')
										: t('game.gameOver')}
						</Text>
						<Text fontSize="5xl" lineHeight="1" fontWeight="black" color="text.default">
							{formatBizySwarmHeight(animatedHeight)}
						</Text>
						<Flex gap={3} wrap="wrap" justify="center">
							<Stat label={t('game.best')} value={formatBizySwarmHeight(result.stats.bestScore)} subtle />
							<Stat label={t('game.dailyBest')} value={formatBizySwarmHeight(result.dailyBestScore)} subtle />
						</Flex>
						<Flex gap={2} wrap="wrap" justify="center">
							<Button size="sm" variant="ghost" colorScheme="secondary" onClick={onClose}>
								{t('game.backToWork')}
							</Button>
							<Button size="sm" colorScheme="primary" onClick={startGame}>
								{t('game.playAgain')}
							</Button>
						</Flex>
						<Text color="text.softer" fontSize="xs">
							{t('game.gameOverRestartHint')}
						</Text>
					</Flex>
				</Flex>
			)}
		</Box>
	);

	return createPortal(game, document.body);
}

function Stat({ label, value, subtle = false }: { label: string; value: string; subtle?: boolean }) {
	return (
		<Flex direction="column" bg={subtle ? 'bg.subtle' : 'bg.surface'} borderRadius="lg" px={3} py={2} minW="74px">
			<Text fontSize="xs" color="text.softer" letterSpacing="0.02em">
				{label}
			</Text>
			<Text fontSize="sm" color="text.default" fontWeight="bold">
				{value}
			</Text>
		</Flex>
	);
}

function PrimaryStat({ label, value, isDanger = false }: { label: string; value: string; isDanger?: boolean }) {
	return (
		<Flex direction="column" minW="96px">
			<Text fontSize="sm" color="text.onColor" letterSpacing="0.02em" opacity={0.78}>
				{label}
			</Text>
			<Text fontSize="3xl" lineHeight="1.05" color="text.onColor" fontWeight="black" opacity={isDanger ? 0.9 : 1}>
				{value}
			</Text>
		</Flex>
	);
}

function IntroRule({ icon, text }: { icon: ReactNode; text: string }) {
	return (
		<HStack spacing={3} align="center">
			<Flex align="center" justify="center" bg="bg.subtle" borderRadius="full" color="text.soft" boxSize="40px" flexShrink={0}>
				{icon}
			</Flex>
			<Text color="text.default" fontSize="sm" fontWeight="normal" lineHeight="tall">
				{text}
			</Text>
		</HStack>
	);
}

function MusicToggleButton({
	isMusicEnabled,
	onToggle,
	musicOnLabel,
	musicOffLabel,
	size = 'md',
}: {
	isMusicEnabled: boolean;
	onToggle: () => void;
	musicOnLabel: string;
	musicOffLabel: string;
	size?: 'sm' | 'md';
}) {
	const Icon = isMusicEnabled ? VolumeUpOutline : VolumeOffOutline;

	return (
		<IconButton
			variant="outline"
			colorScheme="secondary"
			aria-label={isMusicEnabled ? musicOffLabel : musicOnLabel}
			icon={<Icon boxSize={5} />}
			onPointerDown={(event) => event.stopPropagation()}
			onClick={onToggle}
			bg="bg.surface"
			borderColor="border.default"
			borderRadius="xl"
			h={size === 'sm' ? '36px' : '48px'}
			minW={size === 'sm' ? '36px' : '48px'}
			_hover={{ bg: 'bg.subtle' }}
		/>
	);
}

function PickupBurst({ burst }: { burst: PickupBurst }) {
	return (
		<Text
			position="absolute"
			left={`${burst.x}px`}
			top={`${burst.y}px`}
			transform="translate(-50%, -50%)"
			color="text.onColor"
			fontSize="lg"
			fontWeight="black"
			textShadow="0 2px 10px var(--chakra-colors-bg-overlayBackdrop)"
			pointerEvents="none"
			userSelect="none"
			sx={{
				animation: 'bizyPickupBurst 0.75s ease-out forwards',
				'@keyframes bizyPickupBurst': {
					'0%': { opacity: 0, transform: 'translate(-50%, -35%) scale(0.75)' },
					'20%': { opacity: 1, transform: 'translate(-50%, -65%) scale(1.1)' },
					'100%': { opacity: 0, transform: 'translate(-50%, -140%) scale(0.9)' },
				},
			}}
		>
			{burst.label}
		</Text>
	);
}
